import { DateTime, DurationObject } from 'luxon';

const defaultTimeFormatString = 'HH:mm';

/**
 * アプリケーションで扱う時間の変換、計算を扱うクラス.
 */
export class Time {
  constructor(private readonly value: DateTime) {}

  static fromISO(isoString: string): Time {
    return new Time(DateTime.fromISO(isoString));
  }

  static fromJSDate(date: Date): Time {
    return new Time(DateTime.fromJSDate(date));
  }

  eq(other: Time): boolean {
    return this.toString() === other.toString();
  }

  toString(format: string = defaultTimeFormatString): string {
    return this.value.toFormat(format);
  }

  // Default locale で ja を指定すると、日付関連のパフォーマンスが悪化する(内部で Intl API を利用しているため)ので、
  // 曜日文字列取得専用のメソッドを作成しました。
  // See https://github.com/moment/luxon/issues/321.
  getDayOfTheWeekString(locale = 'ja') {
    return this.value.setLocale(locale).toFormat('ccc');
  }

  toUtcISOString(): string {
    return this.value.toUTC().toISO({
      suppressMilliseconds: true,
    });
  }

  plus(duration: DurationObject): Time {
    return new Time(this.value.plus(duration));
  }

  getHour(): number {
    return this.value.hour;
  }

  hasSameYearMonth(other: Time): boolean {
    return (
      this.value.hasSame(other.value, 'year') &&
      this.value.hasSame(other.value, 'month')
    );
  }

  // 現在時刻との差を、分単位で返す.
  // 現在時刻より value が前だったら負の数値、value が後だったら正の数値を返す.
  durationMinutesFromNow(): number {
    return this.value.diffNow('minutes').minutes * -1;
  }

  // 午前0時からの経過秒数を返す.
  durationSecondsFromMidnight(): number {
    return this.value.hour * 60 * 60 + this.value.minute * 60;
  }
}
